import { useNavigate } from '@tanstack/react-router';
import { flexRender, getCoreRowModel, type Row, useReactTable } from '@tanstack/react-table';
import { useGetSubscription } from '@/api/subscription/queries';
import type { GetUsersResponseDto, User } from '@/api/users/types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getColumns } from './columns';
import { mockData } from './config';
import { Pagination } from './pagination';

type Props = {
  data: GetUsersResponseDto;
  pageSize: number;
  metaKey?: string;
  onPageSizeChange: (value: number) => void;
  onPaginationChange: (value: 'next' | 'prev') => void;
};

export function UsersDataTable({
  data,
  pageSize,
  metaKey,
  onPageSizeChange,
  onPaginationChange,
}: Props) {
  'use no memo';
  const { data: subscription } = useGetSubscription();
  const navigate = useNavigate();
  const users = subscription?.isFree ? mockData : data?.data || [];

  const table = useReactTable({
    data: users,
    columns: getColumns(metaKey),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
  });

  const handleRowClick = (row: Row<User>) => {
    navigate({
      to: '/projects/$projectId/users/$userId',
      params: { projectId: row.original.projectId, userId: row.original.publicId },
    });
  };

  return (
    <>
      <Table containerClassName="-mx-4 w-auto px-4">
        <TableHeader className="sticky top-0 bg-background">
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id} className={header.column.columnDef.meta?.className}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              style={{ cursor: 'pointer' }}
              onClick={() => handleRowClick(row)}
              key={row.id}
              data-state={row.getIsSelected() && 'selected'}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className={cell.column.columnDef.meta?.className}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!subscription?.isFree && (
        <div className="flex items-center justify-end flex-shrink-0 space-x-2 py-4">
          <Pagination
            pageSize={pageSize}
            onPaginationChange={onPaginationChange}
            onPageSizeChange={onPageSizeChange}
            hasPrev={Boolean(data?.prev)}
            hasNext={Boolean(data?.next)}
          />
        </div>
      )}
    </>
  );
}
